import React, { useState } from 'react';
import { Card } from '@components/atoms/Card';
import { Button } from '@components/atoms/Button';
import Alert from '@components/atoms/Alert';
import { PaymentMethod as PaymentMethodType } from '@models/subscription';
import {
  CreditCard,
  Building2,
  Smartphone,
  Plus,
  Trash2,
  Check,
  AlertTriangle,
  Star,
} from 'lucide-react';
import { Modal } from '@components/molecules/Modal';
import PaymentMethodForm from './PaymentMethodForm';
import { useTrialStatus } from '@hooks/useTrialStatus';
import { useTranslation } from '@hooks/useTranslation';

interface PaymentMethodsProps {
  paymentMethods: PaymentMethodType[];
  onAddPaymentMethod: (data: any) => Promise<void>;
  onDeletePaymentMethod: (paymentMethodId: string) => Promise<void>;
  onSetDefaultPaymentMethod: (paymentMethodId: string) => Promise<void>;
  isLoading?: boolean;
  /**
   * Open the add-card modal on mount. Set when the user arrived here to pay — from the
   * trial banner or the trial-ending email — so they land on the form instead of on a
   * page where they still have to find the button.
   */
  autoOpenAddModal?: boolean;
}

const PaymentMethods: React.FC<PaymentMethodsProps> = ({
  paymentMethods,
  onAddPaymentMethod,
  onDeletePaymentMethod,
  onSetDefaultPaymentMethod,
  isLoading = false,
  autoOpenAddModal = false,
}) => {
  // Read from the same hook the sidebar pill and the write-gate use, rather than
  // taking a prop: three copies of "is this trial lapsed" is how they drift apart.
  const { isTrialExpired } = useTrialStatus();
  const t = useTranslation('billing');
  const tEnumLabels = useTranslation('enumLabels');
  const tCommon = useTranslation('common');

  /**
   * A lapsed trial with no card is the one state where the empty payment-methods
   * panel is not informational — it is the reason every write in the product is
   * being refused. It gets the red treatment; every other empty state stays calm.
   */
  const isBlockedWithoutCard = isTrialExpired && paymentMethods.length === 0;

  const [showAddModal, setShowAddModal] = useState(autoOpenAddModal);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [paymentMethodToDelete, setPaymentMethodToDelete] =
    useState<PaymentMethodType | null>(null);

  const getPaymentMethodIcon = (type: string) => {
    switch (type) {
      case 'credit_card':
        return <CreditCard className="w-5 h-5" />;
      case 'ach':
        return <Building2 className="w-5 h-5" />;
      case 'digital_wallet':
        return <Smartphone className="w-5 h-5" />;
      default:
        return <CreditCard className="w-5 h-5" />;
    }
  };

  const getPaymentMethodLabel = (type: string, brand?: string) => {
    switch (type) {
      case 'credit_card':
        return brand || tEnumLabels.paymentMethodTypeCreditCard;
      case 'ach':
        return tEnumLabels.paymentMethodTypeAch;
      case 'digital_wallet':
        return tEnumLabels.paymentMethodTypeDigitalWallet;
      default:
        return type;
    }
  };

  const openDeleteModal = (method: PaymentMethodType) => {
    setPaymentMethodToDelete(method);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setPaymentMethodToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!paymentMethodToDelete) return;

    setDeletingId(paymentMethodToDelete.paymentMethodId);
    try {
      await onDeletePaymentMethod(paymentMethodToDelete.paymentMethodId);
      closeDeleteModal();
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddPaymentMethod = async (data: any) => {
    await onAddPaymentMethod(data);
    setShowAddModal(false);
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    setSettingDefaultId(paymentMethodId);
    try {
      await onSetDefaultPaymentMethod(paymentMethodId);
    } finally {
      setSettingDefaultId(null);
    }
  };

  return (
    <>
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t.paymentMethods.title}
          </h2>
          {/* Hidden while blocked: the red alert below carries the only call to
              action, so the eye is not split between two identical buttons. */}
          {paymentMethods.length === 0 && !isBlockedWithoutCard && (
            <Button
              variant="primary"
              size="md"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShowAddModal(true)}>
              {t.paymentMethods.addButton}
            </Button>
          )}
        </div>

        {isBlockedWithoutCard ? (
          <Alert
            variant="error"
            showIcon
            title={t.paymentMethods.lockedTitle}>
            <p>
              {t.paymentMethods.lockedBodyPrefix}{' '}
              <strong>{t.paymentMethods.lockedBodyEmphasis}</strong>{' '}
              {t.paymentMethods.lockedBodySuffix}
            </p>
            <p className="mt-2">
              {t.paymentMethods.lockedDataSafe}
            </p>
            <Button
              variant="primary"
              size="md"
              className="mt-3"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShowAddModal(true)}>
              {t.paymentMethods.addNowButton}
            </Button>
          </Alert>
        ) : paymentMethods.length === 0 ? (
          <Alert variant="info" showIcon title={t.paymentMethods.noPaymentMethodsTitle}>
            <p>
              {t.paymentMethods.noPaymentMethodsBody}
            </p>
          </Alert>
        ) : (
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <div
                key={method.paymentMethodId}
                className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-400">
                    {getPaymentMethodIcon(method.type)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {getPaymentMethodLabel(method.type, method.brand)}
                      </p>
                      {method.isDefault && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs font-medium rounded-full">
                          <Check className="w-3 h-3" />
                          {t.paymentMethods.defaultBadge}
                        </span>
                      )}
                    </div>
                    {method.last4 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        •••• {method.last4}
                        {method.expiryMonth &&
                          method.expiryYear &&
                          ` · ${t.paymentMethods.expiresSuffix
                            .replace('{month}', String(method.expiryMonth))
                            .replace('{year}', String(method.expiryYear))}`}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!method.isDefault && (
                    <Button
                      variant="tertiary"
                      size="md"
                      leftIcon={<Star className="w-4 h-4" />}
                      onClick={() => handleSetDefault(method.paymentMethodId)}
                      disabled={
                        isLoading || settingDefaultId === method.paymentMethodId
                      }
                      loading={settingDefaultId === method.paymentMethodId}
                      className="text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 dark:text-gray-400 dark:hover:text-indigo-400 dark:hover:bg-indigo-900/20">
                      {t.paymentMethods.setAsDefaultButton}
                    </Button>
                  )}
                  <Button
                    variant="tertiary"
                    size="md"
                    onlyIcon
                    onClick={() => openDeleteModal(method)}
                    disabled={
                      isLoading || deletingId === method.paymentMethodId
                    }
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                    title={t.paymentMethods.deleteButtonTitle}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={t.paymentMethods.addButton}
        size="large">
        <PaymentMethodForm
          onSubmit={handleAddPaymentMethod}
          onCancel={() => setShowAddModal(false)}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        size="small"
        showCloseButton={false}>
        <div className="flex flex-col items-center text-center py-4">
          {/* Warning Icon */}
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-5">
            <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>

          {/* Title */}
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t.paymentMethods.deleteModalTitle}
          </h3>

          {/* Description */}
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {t.paymentMethods.deleteConfirmBody}
          </p>

          {/* Payment Method Details */}
          {paymentMethodToDelete && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-4 py-3 mb-4 w-full">
              <div className="flex items-center justify-center gap-3">
                <div className="p-2 bg-white dark:bg-gray-600 rounded-lg">
                  {getPaymentMethodIcon(paymentMethodToDelete.type)}
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {getPaymentMethodLabel(
                      paymentMethodToDelete.type,
                      paymentMethodToDelete.brand,
                    )}
                  </p>
                  {paymentMethodToDelete.last4 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      •••• {paymentMethodToDelete.last4}
                      {paymentMethodToDelete.expiryMonth &&
                        paymentMethodToDelete.expiryYear &&
                        ` · ${t.paymentMethods.expSuffix
                          .replace('{month}', String(paymentMethodToDelete.expiryMonth))
                          .replace('{year}', String(paymentMethodToDelete.expiryYear))}`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Warning Message */}
          <p className="text-sm text-red-600 dark:text-red-400 mb-6">
            {t.paymentMethods.deleteWarning}
          </p>

          {/* Action Buttons */}
          <div className="flex gap-3 w-full">
            <Button
              variant="secondary"
              size="md"
              onClick={closeDeleteModal}
              disabled={deletingId !== null}
              className="flex-1">
              {tCommon.cancel}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleConfirmDelete}
              loading={deletingId !== null}
              className="flex-1 !bg-red-600 hover:!bg-red-700 !border-red-600 hover:!border-red-700">
              {tCommon.delete}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PaymentMethods;
